/**
 * FHEVM Instance Manager for ShadeMarket
 * Uses Zama Relayer SDK v0.3.0-5 for FHE encryption/decryption
 * FHEVM v0.9 Compatible
 */

let fheInstance = null;
let storedKeypair = null;

const KEYPAIR_STORAGE_KEY = 'silentbid_fhe_keypair';

/**
 * Initialize the FHE instance
 */
export async function initializeFheInstance() {
    if (typeof window === 'undefined' || !window.ethereum) {
        throw new Error('Ethereum provider not found. Please install MetaMask.');
    }

    try {
        
        // Try both possible global names
        let sdk = window.RelayerSDK || window.relayerSDK;
        
        if (!sdk) {
            console.error('❌ window.RelayerSDK not found!');
            throw new Error('RelayerSDK not loaded. Check index.html script tag.');
        }
        
        
        const { initSDK, createInstance, SepoliaConfig, generateKeypair } = sdk;
        
        if (!initSDK || !createInstance || !SepoliaConfig) {
            throw new Error('SDK exports incomplete.');
        }
        
        await initSDK();
        
        // Check network
        const chainId = await window.ethereum.request({ method: 'eth_chainId' });
        
        if (chainId !== '0xaa36a7') {
            console.warn('⚠️  Not on Sepolia testnet. Current chain:', chainId);
        }
        
        // Generate or retrieve keypair
        let keypair;
        const stored = localStorage.getItem(KEYPAIR_STORAGE_KEY);
        
        if (stored) {
            try {
                keypair = JSON.parse(stored);
            } catch (e) {
                keypair = generateKeypair();
                localStorage.setItem(KEYPAIR_STORAGE_KEY, JSON.stringify(keypair));
            }
        } else {
            keypair = generateKeypair();
            localStorage.setItem(KEYPAIR_STORAGE_KEY, JSON.stringify(keypair));
        }
        
        storedKeypair = keypair;
        
        const config = {
            ...SepoliaConfig,
            network: window.ethereum,
            keypair: keypair,
            relayerUrl: 'https://relayer.testnet.zama.org'
        };
        
        fheInstance = await createInstance(config);
        
        return fheInstance;
    } catch (err) {
        console.error('❌ FHE initialization failed:', err);
        throw new Error('Failed to initialize FHE: ' + (err?.message || 'Unknown error'));
    }
}

/**
 * Public decryption via ZAMA Relayer SDK
 * Used for settlement - decrypts ciphertexts marked as publicly decryptable
 * @param {string[]} handles - Array of encrypted ciphertext handles (bytes32)
 * @returns {Object} { clearValues, abiEncodedClearValues, decryptionProof }
 */
export async function publicDecrypt(handles) {
    const fhe = getFheInstance();
    
    if (!handles || handles.length === 0) {
        throw new Error('No handles provided for decryption');
    }

    console.log('📤 publicDecrypt called with handles:', handles);

    try {
        // Call the Relayer SDK's publicDecrypt function
        // This contacts the ZAMA KMS to decrypt publicly-decryptable ciphertexts
        const results = await fhe.publicDecrypt(handles);
        
        console.log('📥 publicDecrypt raw results:', results);
        console.log('  - clearValues:', results.clearValues || results.values);
        console.log('  - abiEncodedClearValues:', results.abiEncodedClearValues);
        console.log('  - decryptionProof:', results.decryptionProof?.slice(0, 100) + '...');
        
        return {
            clearValues: results.clearValues || results.values,
            abiEncodedClearValues: results.abiEncodedClearValues,
            decryptionProof: results.decryptionProof
        };
    } catch (error) {
        console.error('❌ Public decryption failed:', error);
        throw error;
    }
}

/**
 * Get the current FHE instance
 */
export function getFheInstance() {
    if (!fheInstance) {
        throw new Error('FHE instance not initialized. Call initializeFheInstance() first.');
    }
    return fheInstance;
}

/**
 * Encrypt a bid price (wei value as BigInt)
 * @param {BigInt|string} priceWei - Price per token in wei
 * @param {string} contractAddress - ShadeMarket contract address
 * @param {string} userAddress - Bidder's address
 * @returns {Object} { data, proof } for contract call
 */
export async function encryptBidPrice(priceWei, contractAddress, userAddress) {
    const fhe = getFheInstance();

    
    const priceBigInt = BigInt(priceWei);
    
    const input = fhe.createEncryptedInput(contractAddress, userAddress);
    input.add64(priceBigInt);
    const encrypted = await input.encrypt();
    

    return {
        data: encrypted.handles[0],
        proof: encrypted.inputProof
    };
}

/**
 * Request decryption of encrypted price (for settlement/viewing)
 */
export async function decryptBidPrice(encryptedHandle, contractAddress, userAddress) {
    const fhe = getFheInstance();
    const sdk = window.RelayerSDK || window.relayerSDK;


    try {
        const keypair = fhe.generateKeypair();
        
        const startTimeStamp = Math.floor(Date.now() / 1000).toString();
        const durationDays = '10';
        
        const eip712 = fhe.createEIP712(
            keypair.publicKey,
            [contractAddress],
            startTimeStamp,
            durationDays
        );
        
        
        const ethers = window.ethers || (await import('ethers')).ethers;
        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        
        const signature = await signer.signTypedData(
            eip712.domain,
            { UserDecryptRequestVerification: eip712.types.UserDecryptRequestVerification },
            eip712.message
        );
        
        
        const checksummedAddress = ethers.getAddress(userAddress);
        
        const result = await fhe.userDecrypt(
            [{ handle: encryptedHandle, contractAddress }],
            keypair.privateKey,
            keypair.publicKey,
            signature.replace('0x', ''),
            [contractAddress],
            checksummedAddress,
            startTimeStamp,
            durationDays
        );
        
        return result[encryptedHandle];
    } catch (error) {
        console.error('❌ Decryption failed:', error);
        throw error;
    }
}

/**
 * Clear stored keypair
 */
export function clearKeypair() {
    localStorage.removeItem(KEYPAIR_STORAGE_KEY);
    storedKeypair = null;
    fheInstance = null;
}
